import gql from "graphql-tag";
import { hasRenditions, renditionUrl } from "~/utils/cdn";
import { toCrawlableInternalPath } from "./canonicalPath";

/**
 * This stays independent of the main product query. An installation without
 * the Model Variants extension can reject this optional request without
 * taking the PDP down with it.
 */
export const MODEL_VARIANTS_QUERY = gql`
  query ModelVariantsV1($productId: Int!) {
    modelVariantsV1(productId: $productId) {
      schemaVersion
      status
      statusReason
      group {
        id
        revision
        manufacturerId
        model
        label
        members {
          id
          name
          model
          manufacturerId
          url
          image
          stock
          colorCode
          frameColor
          lensColor
          productType
          size
          current
        }
      }
    }
  }
`;

export type ModelVariantLink = {
  id: string;
  name?: string;
  path: string;
  label: string;
  image: string;
  stock: boolean;
  isCurrent: boolean;
};

export type ModelVariantsState = {
  status: "ready" | "unavailable";
  groupKey: string;
  groupLabel: string;
  displayModel: string;
  current: ModelVariantLink | null;
  variants: ModelVariantLink[];
  availableAlternatives: ModelVariantLink[];
};

type VariantMember = {
  id?: unknown;
  name?: unknown;
  model?: unknown;
  manufacturerId?: unknown;
  url?: unknown;
  image?: unknown;
  stock?: unknown;
  colorCode?: unknown;
  frameColor?: unknown;
  lensColor?: unknown;
  productType?: unknown;
  size?: unknown;
  current?: unknown;
};

type VariantGroup = {
  id?: unknown;
  revision?: unknown;
  manufacturerId?: unknown;
  model?: unknown;
  label?: unknown;
  members?: unknown;
};

type ProductIdentity = {
  id?: unknown;
  manufacturerId?: unknown;
  manufacturer?: { id?: unknown } | null;
};

const collapsed = (value: unknown) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();
const normalizeId = (value: unknown) => String(value ?? "").trim();
const positiveInteger = (value: unknown): number | null => {
  const normalized = normalizeId(value);
  if (!/^\d+$/.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
};
const productManufacturerId = (product: ProductIdentity | null | undefined) =>
  normalizeId(product?.manufacturerId ?? product?.manufacturer?.id);

const tileImageForMember = (image: unknown): string => {
  const raw = typeof image === "string" ? image.trim() : "";
  if (!raw || (!raw.startsWith("https://") && !/^\/(?!\/)/.test(raw))) {
    return "";
  }
  return hasRenditions(raw)
    ? renditionUrl(raw, 600, { ratio: 1, format: "avif" })
    : raw;
};

const memberLabel = (member: VariantMember): string => {
  const colors = Array.from(
    new Set(
      [member.colorCode, member.frameColor, member.lensColor]
        .map(collapsed)
        .filter(Boolean)
    )
  );
  const facts = [
    ...colors,
    collapsed(member.productType),
    collapsed(member.size),
  ]
    .filter(Boolean)
    .join(" · ");
  return facts || collapsed(member.name);
};

export function buildModelVariantsVariables(productId: unknown) {
  return { productId: positiveInteger(productId) };
}

export function unavailableModelVariants(groupKey = ""): ModelVariantsState {
  return {
    status: "unavailable",
    groupKey,
    groupLabel: "",
    displayModel: "",
    current: null,
    variants: [],
    availableAlternatives: [],
  };
}

/**
 * Accept only complete, extension-owned responses. We deliberately do not
 * reconstruct a group from product fields if the extension is absent or a
 * response is malformed: persisted backend membership is the source of truth.
 */
export function resolveModelVariants(
  currentProductId: unknown,
  data: unknown,
  currentProduct?: ProductIdentity | null
): ModelVariantsState {
  const currentId = normalizeId(currentProductId);
  const payload = (data as { modelVariantsV1?: unknown })?.modelVariantsV1 as
    | { schemaVersion?: unknown; status?: unknown; group?: VariantGroup | null }
    | undefined;
  const group = payload?.group;
  const requestedManufacturerId = productManufacturerId(currentProduct);

  if (
    !currentId ||
    payload?.schemaVersion !== 1 ||
    payload?.status !== "OK" ||
    !group ||
    !normalizeId(group.id) ||
    positiveInteger(group.revision) === null ||
    !normalizeId(group.manufacturerId) ||
    (requestedManufacturerId &&
      normalizeId(group.manufacturerId) !== requestedManufacturerId) ||
    !Array.isArray(group.members)
  ) {
    return unavailableModelVariants();
  }

  const groupManufacturerId = normalizeId(group.manufacturerId);
  const seen = new Set<string>();
  const validMembers: Array<ModelVariantLink & { sourceName: string }> = [];
  let currentCount = 0;

  for (const rawMember of group.members as VariantMember[]) {
    const id = normalizeId(rawMember?.id);
    const isCurrent = rawMember?.current === true;
    if (
      !id ||
      seen.has(id) ||
      normalizeId(rawMember?.manufacturerId) !== groupManufacturerId
    ) {
      return unavailableModelVariants(normalizeId(group.id));
    }
    seen.add(id);
    if (isCurrent) currentCount++;

    const path = toCrawlableInternalPath(rawMember?.url);
    // Current is a non-linking tile; every alternative needs a safe internal
    // destination before it can be displayed as a crawlable link.
    if ((!isCurrent && !path) || !memberLabel(rawMember)) {
      return unavailableModelVariants(normalizeId(group.id));
    }
    validMembers.push({
      id,
      path,
      label: memberLabel(rawMember),
      image: tileImageForMember(rawMember?.image),
      stock: rawMember?.stock === true,
      isCurrent,
      sourceName: collapsed(rawMember?.name),
    });
  }

  const current = validMembers.find((member) => member.isCurrent) || null;
  if (
    validMembers.length < 2 ||
    currentCount !== 1 ||
    !current ||
    current.id !== currentId
  ) {
    return unavailableModelVariants(normalizeId(group.id));
  }

  // Colour/type/size are the primary label. If two SKUs share those facts,
  // add their catalog names so a tooltip and no-image tile remain distinct.
  const labelCounts = validMembers.reduce((counts, member) => {
    counts.set(member.label, (counts.get(member.label) || 0) + 1);
    return counts;
  }, new Map<string, number>());
  const variants = validMembers
    .map(({ sourceName, ...member }) => ({
      ...member,
      name: sourceName,
      label:
        (labelCounts.get(member.label) || 0) > 1 && sourceName
          ? `${member.label} · ${sourceName}`
          : member.label,
    }))
    .sort((left, right) => Number(right.stock) - Number(left.stock));

  return {
    status: "ready",
    groupKey: normalizeId(group.id),
    groupLabel: collapsed(group.label) || collapsed(group.model),
    displayModel: collapsed(group.model),
    current: variants.find((member) => member.isCurrent) || null,
    variants,
    availableAlternatives: variants.filter(
      (member) => !member.isCurrent && member.stock
    ),
  };
}

type ModelVariantsFetch = (options: {
  query: typeof MODEL_VARIANTS_QUERY;
  variables: { productId: number | null };
}) => Promise<unknown> | unknown;

export function createModelVariantsFetch(
  apolloClient: any
): ModelVariantsFetch {
  return async (options) => {
    if (typeof apolloClient?.query !== "function") return null;
    const result = await apolloClient.query({
      query: options.query,
      variables: options.variables,
      // Do not reuse Apollo state after an admin changes a durable group.
      fetchPolicy: "network-only",
      errorPolicy: "none",
    });
    return result?.data ?? null;
  };
}

export async function loadModelVariants(
  fetchData: ModelVariantsFetch,
  product: ProductIdentity | null | undefined
): Promise<ModelVariantsState | null> {
  const variables = buildModelVariantsVariables(product?.id);
  if (variables.productId === null) return null;

  try {
    const data = await fetchData({ query: MODEL_VARIANTS_QUERY, variables });
    return data
      ? resolveModelVariants(product?.id, data, product)
      : unavailableModelVariants();
  } catch {
    return unavailableModelVariants();
  }
}
